import React, { useState , useContext} from 'react';
import { User, MessageSquare, Clock, File, MessageCircle, UserMinus, Headphones, UserCheck } from 'react-feather';
import { ConfigContext } from '../../../../Api/global/ApiContext';
import { addNewQaList, getTeachers } from '../../../../Api/apiUtils';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';
import { Badge, Button, Card, Field, Select, TextInput } from '../../../../ui';
import TextArea from './ui-pending/TextArea';
import './addQa.scss';

/**
 * One score input. `controlled: false` fields have no value prop (as before), and
 * `validate: false` fields show no 0–5 range message (as before).
 */
type ScoreField = {
  name: string;
  label: string;
  type: 'text' | 'number';
  controlled: boolean;
  validate: boolean;
  placeholder?: string;
};
type ScoreSection = { title: string; icon: React.ReactNode; fields: ScoreField[] };

const P = 'Enter 0 to 5';
const SCORE_SECTIONS: ScoreSection[] = [
  {
    title: 'Opening',
    icon: <Clock size={18} aria-hidden />,
    fields: [
      { name: 'Opening.startedOnTime', label: 'Started on Time', type: 'text', controlled: true, validate: true, placeholder: P },
      { name: 'Opening.internetConnection', label: 'Internet Connection', type: 'text', controlled: true, validate: true, placeholder: P },
      { name: 'Opening.greetingIntroduction', label: 'Greeting/Introduction', type: 'text', controlled: true, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Feedback',
    icon: <MessageSquare size={18} aria-hidden />,
    fields: [
      { name: 'Feedback.constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass', label: 'Assignment of the Previous Class', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'Feedback.aOIWithExamples', label: 'AOI with Examples', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'Feedback.addressTheStudentByTheirName', label: 'Address the Student by Their Name', type: 'number', controlled: true, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Rapport Building',
    icon: <File size={18} aria-hidden />,
    fields: [
      { name: 'RapportBuilding.engagementLevel', label: 'Engagement Level', type: 'text', controlled: true, validate: true, placeholder: P },
      { name: 'RapportBuilding.madeTheStudentFeelComfortable', label: 'Made the Student Feel Comfortable', type: 'text', controlled: true, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Communication Skills and Soft Skills',
    icon: <MessageCircle size={18} aria-hidden />,
    fields: [
      { name: 'communicationSkillsAndSoftskills.deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided', label: 'Dead Air/Fumbling/Fillers/Unnecessary Mute Needs to be Avoided', type: 'number', controlled: true, validate: false, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.casualApproachToneUsageOfSlangLanguage', label: 'Casual Approach/Tone, Usage of Slang Language', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.acknowledgment', label: 'Acknowledgment', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.activeListening', label: 'Active Listening', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.interruption', label: 'Interruption', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.energyLevel', label: 'Energy Level', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'communicationSkillsAndSoftskills.rateOfSpeech', label: 'Rate of Speech', type: 'number', controlled: true, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Closing',
    icon: <UserMinus size={18} aria-hidden />,
    fields: [
      { name: 'Closing.customerDelight', label: 'Customer Delight', type: 'number', controlled: true, validate: true, placeholder: P },
      { name: 'Closing.DoubtsSolvingQueryResolution', label: 'Doubts Solving/Query Resolution', type: 'text', controlled: true, validate: true, placeholder: P },
      { name: 'Closing.energeticAndPositiveClosing', label: 'Energetic and Positive Closing', type: 'text', controlled: true, validate: true, placeholder: P },
      { name: 'Closing.disconnectionAfterTheStudent', label: 'Disconnection after the Student', type: 'text', controlled: true, validate: false, placeholder: P },
    ],
  },
  {
    title: 'Tech & Gadgets',
    icon: <Headphones size={18} aria-hidden />,
    fields: [
      { name: 'TechGadgets.mic', label: 'Mic', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'TechGadgets.headphones', label: 'Headphones', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'TechGadgets.jacket', label: 'Jacket', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'TechGadgets.videoQualityCamAngleBodyLanguageBackground', label: 'Video Quality/Cam Angle/Body Language/Background', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'TechGadgets.practiceStudio', label: 'Practice Studio', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'TechGadgets.pleaseSpecifyTheInstrumentUsed', label: 'Instrument Used', type: 'text', controlled: false, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Presentation',
    icon: <UserCheck size={18} aria-hidden />,
    fields: [
      { name: 'Presentation.appropriatelyDressed', label: 'Appropriately Dressed', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'Presentation.videoOn', label: 'Video On', type: 'text', controlled: false, validate: true, placeholder: P },
      { name: 'Presentation.rudeSarcasticIntimidatingBehaviourUnprofessionalism', label: 'Rude/Sarcastic/Intimidating Behaviour/Unprofessionalism', type: 'text', controlled: false, validate: true, placeholder: P },
    ],
  },
  {
    title: 'Time',
    icon: <Clock size={18} aria-hidden />,
    fields: [
      { name: 'Time.sessionAsPerStandardTime', label: 'Session as per Standard Time', type: 'text', controlled: false, validate: true, placeholder: P },
      // Top-level observation, repeated here as in the original form.
      { name: 'observation', label: 'Observation', type: 'text', controlled: true, validate: false },
    ],
  },
];

/** The 0–5 range message shown under score inputs (same rule as before). */
const scoreRangeError = (value: string) => {
  const num = parseFloat(value);
  if (value !== "" && (isNaN(num) || num < 0 || num > 5)) {
    return "Please enter a value between 0 to 5";
  }
  return undefined;
};

const getPath = (obj: any, path: string) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const SectionTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <span className="tp-addqa__section-title">
    {icon}
    {children}
  </span>
);


interface addQaProps {
    teacherName:any;
    teacherId:any
    fetchQaListData:any
    onSuccess?: () => void
}

interface Teacher {
    teacherId: any;
    teacherName: string;
    credentialId: any;

  }
const AddQaList: React.FC<addQaProps> = ({teacherName, teacherId, fetchQaListData, onSuccess}) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        // teacherId: '',
        teacherName: teacherName,
        teacherCategory: '',
        studentName: '',
        sessionDate: '',
        course: '',
        ageGroup: '',
        auditDate: '',
        feedbackStatus: '',
        feedbackLink: '',
        observation: '',
 
        Opening: {
            startedOnTime: '',
            internetConnection: '',
            greetingIntroduction: '',
        },
        Feedback: {
            constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass: '',
            aOIWithExamples: '',
            addressTheStudentByTheirName: '',
        },
        RapportBuilding: {
            engagementLevel: '',
            madeTheStudentFeelComfortable: '',
        },
        communicationSkillsAndSoftskills: {
            deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided: '',
            casualApproachToneUsageOfSlangLanguage: '',
            acknowledgment: '',
            activeListening: '',
            interruption: '',
            energyLevel: '',
            rateOfSpeech: '',
        },
        Closing: {
            customerDelight: '',
            DoubtsSolvingQueryResolution: '',
            energeticAndPositiveClosing: '',
            disconnectionAfterTheStudent: '',
        },
        TechGadgets: {
            mic: '',
            headphones: '',
            jacket: '',
            videoQualityCamAngleBodyLanguageBackground: '',
            practiceStudio: '',
            pleaseSpecifyTheInstrumentUsed: '',
        },
        Presentation: {
            appropriatelyDressed: '',
            videoOn: '',
            rudeSarcasticIntimidatingBehaviourUnprofessionalism: '',
        },
        Time: {
            sessionAsPerStandardTime: '',
        },
    });
    const [isLoading, setIsLoading] = useState(false);
    const [teachers, setTeachers] = useState<Teacher[]>([]);

    const config = useContext(ConfigContext);
    const { states = [], genders = [], timezones = [], ageGroups = [], countryCodes = []  , courses = [], languages=[]} = useContext(ConfigContext) || {};
    // const [searchQuery, setSearchQuery] = useState("");
const [isModalOpen, setIsModalOpen] = useState(false);
const [selectedTeacher, setSelectedTeacher] = useState("");
const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
// const handleSearch = async () => {
//   const searchPayload = { name: searchQuery };
//   await fetchTeachers(searchPayload);
//   setIsModalOpen(true); 
// };
const courseOptions = courses.map((course: any) => ({
    value: course.name,
    label: course.name,
  }));



// Function to calculate total score from all numeric fields
const calculateTotalScore = () => {
    let total = 0;
    
    // Helper function to sum numeric values from nested objects
    const sumNumericValues = (obj: any): number => {
        let sum = 0;
        for (const key in obj) {
            if (typeof obj[key] === "object" && obj[key] !== null) {
                sum += sumNumericValues(obj[key]);
            } else if (typeof obj[key] === "string" && obj[key].trim() !== "") {
                const num = parseFloat(obj[key]);
                if (!isNaN(num) && num >= 0 && num <= 5) {
                    sum += num;
                }
            }
        }
        return sum;
    };

    // Calculate sum from all sections
    total += sumNumericValues(formData.Opening);
    total += sumNumericValues(formData.Feedback);
    total += sumNumericValues(formData.RapportBuilding);
    total += sumNumericValues(formData.communicationSkillsAndSoftskills);
    total += sumNumericValues(formData.Closing);
    total += sumNumericValues(formData.TechGadgets);
    total += sumNumericValues(formData.Presentation);
    total += sumNumericValues(formData.Time);

    return total;
};




const handleAddTeacher = () => {
    if (selectedTeacher) {
      setFormData((prevState) => ({
        ...prevState,
        teacherName: selectedTeacher, 
      }));
      setIsModalOpen(false); 
      setSelectedTeacher(""); 
    } else {
      toast.warning("Please select a teacher.");
    }
  };
  
  // Function to toggle modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

   
      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
    

        const keys = name.split(".");
            setFormData((prevState) => {
          const newState = { ...prevState };
    

          let currentLevel: any = newState; 
          for (let i = 0; i < keys.length - 1; i++) {
            if (!currentLevel[keys[i]]) {
              currentLevel[keys[i]] = {}; 
            }
            currentLevel = currentLevel[keys[i]];
          }
    
          currentLevel[keys[keys.length - 1]] = value;
    
          return newState;
        });
      };
    

  
    const isFormValid = () => {
      const missingFields: string[] = [];

      // Helper to check nested objects
      const checkFields = (obj: any, parentKey: string = '') => {
        let isValid = true;
        for (const key in obj) {
          const fullKey = parentKey ? `${parentKey}.${key}` : key;
          if (typeof obj[key] === "object" && obj[key] !== null) {
            if (!checkFields(obj[key], fullKey)) {
              isValid = false;
            }
          } else {
            const value = typeof obj[key] === "string" ? obj[key].trim() : obj[key];
            if (value === "" || value === null || value === undefined) {
              missingFields.push(fullKey);
              isValid = false;
            }
          }
        }
        return isValid;
      };

      const {
        Opening,
        Feedback,
        RapportBuilding,
        communicationSkillsAndSoftskills,
        Closing,
        TechGadgets,
        Presentation,
        Time,
        ...basicFields
      } = formData;

      // Check basic fields
      for (const key in basicFields) {
        const value = typeof basicFields[key as keyof typeof basicFields] === "string"
          ? (basicFields[key as keyof typeof basicFields] as string).trim()
          : basicFields[key as keyof typeof basicFields];
        if (value === "" || value === null || value === undefined) {
          missingFields.push(key);
        }
      }

      // Check all nested objects
      checkFields(Opening, 'Opening');
      checkFields(Feedback, 'Feedback');
      checkFields(RapportBuilding, 'RapportBuilding');
      checkFields(communicationSkillsAndSoftskills, 'communicationSkillsAndSoftskills');
      checkFields(Closing, 'Closing');
      checkFields(TechGadgets, 'TechGadgets');
      checkFields(Presentation, 'Presentation');
      checkFields(Time, 'Time');

      if (missingFields.length > 0) {
        // toast.error(`Missing fields: ${missingFields.join(', ')}`);
        return false;
      }

      // Check total score validation
      const totalScore = calculateTotalScore();
      if (totalScore > 100) {
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      // Check total score first
      const totalScore = calculateTotalScore();
      if (totalScore > 100) {
        toast.error(`Total score (${totalScore}) exceeds 100. Please reduce some values to proceed.`);
        return;
      }

      if (!isFormValid()) {
        toast.error("All fields are mandatory. Please fill in all fields.");
        return;
      }
    

      const {
        Opening,
        Feedback,
        RapportBuilding,
        communicationSkillsAndSoftskills,
        Closing,
        TechGadgets,
        Presentation,
        Time,
        ...basicFields
      } = formData;

      const updatedFormData = {
        ...basicFields,
        teacherId: teacherId,
        payload: [
          {
            Opening,
            Feedback,
            RapportBuilding,
            communicationSkillsAndSoftskills,
            Closing,
            TechGadgets,
            Presentation,
            Time
          }
        ]
      };
      
      setIsLoading(true);
      try {
        await addNewQaList(updatedFormData);
        setIsLoading(false);
       toast.success('QA List added successfully');
       trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_CREATED, {
         source: "teacher_profile",
         teacherId: teacherId != null ? String(teacherId) : undefined,
       });
        if (onSuccess) {
         onSuccess();
       } else if (fetchQaListData) {
         fetchQaListData();
       }
      } catch (error) {
        console.error("Error adding QA List:", error);
        setIsLoading(false);
        trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_CREATE_FAILED, {
          source: "teacher_profile",
          teacherId: teacherId != null ? String(teacherId) : undefined,
        });
      }
    };

    const total = calculateTotalScore();

    return (
        <div className="tp-addqa">
            <div className="tp-addqa__scroll">
                <Card title={<SectionTitle icon={<User size={18} aria-hidden />}>Basic Information</SectionTitle>}>
                    <div className="tp-addqa__grid">
                        <Field label="Teacher Name">
                            {(p) => <TextInput {...p} disabled type="text" name="teacherName" value={teacherName} onChange={handleChange} />}
                        </Field>
                        <span className="tp-addqa__spacer" aria-hidden />
                        <Field label="Teacher Category">
                            {(p) => <TextInput {...p} type="text" name="teacherCategory" value={formData.teacherCategory} onChange={handleChange} />}
                        </Field>
                        <Field label="Student Name">
                            {(p) => <TextInput {...p} type="text" name="studentName" value={formData.studentName} onChange={handleChange} />}
                        </Field>
                        <Field label="Age Group">
                            {(p) => (
                                <Select
                                    {...p}
                                    name="ageGroup"
                                    onChange={handleInputChange}
                                    options={[
                                        { value: "", label: "Select Age Group" },
                                        ...(ageGroups || []).map((ageGroup: any) => ({ value: ageGroup.name, label: ageGroup.name })),
                                    ]}
                                />
                            )}
                        </Field>
                        <Field label="Course">
                            {(p) => (
                                <Select
                                    {...p}
                                    name="course"
                                    onChange={handleInputChange}
                                    options={[
                                        { value: "", label: "Select a course" },
                                        ...(courseOptions || []).map((c: any) => ({ value: c.value, label: c.value })),
                                    ]}
                                />
                            )}
                        </Field>
                        <Field label="Session Date">
                            {(p) => <TextInput {...p} type="date" name="sessionDate" value={formData.sessionDate} onChange={handleChange} />}
                        </Field>
                        <Field label="Audit Date">
                            {(p) => <TextInput {...p} type="date" name="auditDate" value={formData.auditDate} onChange={handleChange} />}
                        </Field>
                        <Field label="Feedback Status">
                            {(p) => <TextInput {...p} name="feedbackStatus" value={formData.feedbackStatus} type="text" onChange={handleChange} />}
                        </Field>
                        <Field label="Feedback Link">
                            {(p) => <TextInput {...p} onChange={handleChange} name="feedbackLink" type="text" value={formData.feedbackLink} />}
                        </Field>
                        <Field label="Observation" className="tp-addqa__full">
                            {(p) => <TextArea {...p} onChange={handleChange as any} name="observation" value={formData.observation} />}
                        </Field>
                    </div>
                </Card>

                {SCORE_SECTIONS.map((section) => (
                    <Card key={section.title} title={<SectionTitle icon={section.icon}>{section.title}</SectionTitle>}>
                        <div className="tp-addqa__grid">
                            {section.fields.map((field) => {
                                const value = String(getPath(formData, field.name) ?? "");
                                return (
                                    <Field
                                        key={field.name}
                                        label={field.label}
                                        error={field.validate ? scoreRangeError(value) : undefined}
                                    >
                                        {(p) => (
                                            <TextInput
                                                {...p}
                                                type={field.type}
                                                name={field.name}
                                                placeholder={field.placeholder}
                                                onChange={handleChange}
                                                {...(field.controlled ? { value } : {})}
                                            />
                                        )}
                                    </Field>
                                );
                            })}
                        </div>
                    </Card>
                ))}
            </div>

            <div className="tp-addqa__footer">
                <div className="tp-addqa__total" aria-live="polite">
                    <Badge tone={total > 100 ? 'danger' : 'success'}>Total Score: {total}/100</Badge>
                    {total > 100 && (
                        <span className="tp-addqa__over">Total score exceeds 100! Please reduce some values.</span>
                    )}
                </div>
                <Button variant="primary" onClick={handleSubmit}>
                    Save
                </Button>
            </div>
        </div>
    );
};

export default AddQaList;
